import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Fingerprint, Lock, LifeBuoy, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { disableTotp, regenerateBackupCodes } from "../../api/security.js";
import { ApiClientError } from "../../api/http.js";
import {
  AUTO_LOCK_OPTIONS,
  CLIPBOARD_OPTIONS,
  HIDDEN_LOCK_GRACE_MS,
  setAutoLockMinutes,
  setBreachCheck,
  setClipboardSeconds,
  setLockOnHide,
  useAutoLockMinutes,
  useBreachCheck,
  useClipboardSeconds,
  useLockOnHide
} from "../../app/preferences.js";
import type { AutoLockMinutes, ClipboardSeconds } from "../../app/preferences.js";
import { clearRecentUse, useRecentUse } from "../../app/recentUse.js";
import { useLockVault } from "../../app/lockVault.js";
import { isDesktop } from "../../app/platform.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { useSession } from "../../app/session.js";
import { ME_KEY, usePolicies } from "../../data/account.js";
import { allowedLockOptions, cappedAutoLock } from "../../api/policies.js";
import { PASSKEYS_KEY, TWO_FACTOR_KEY, useTwoFactorStatus } from "../../data/security.js";
import { formatRelativeTime, formatDateTime } from "../../lib/format.js";
import { isCompleteOtp } from "../../lib/otp.js";
import { Dialog } from "../../ui/Dialog.js";
import { Field, SelectInput, Switch, TextInput } from "../../ui/forms.js";
import { Alert, Badge, Button, IconButton, Page, PageHeader, Section, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { AdminRecoverySection } from "../recovery/AdminRecoverySection.js";
import { RecoverySection } from "../recovery/RecoverySection.js";
import { DeviceApprovalsSection } from "./DeviceApprovalsSection.js";
import { ExportSection } from "./ExportSection.js";
import { MasterPasswordSection } from "./MasterPasswordSection.js";
import { PasskeysSection } from "./PasskeysSection.js";
import { RememberedDevicesSection } from "./RememberedDevicesSection.js";
import { SessionsSection } from "./SessionsSection.js";
import { TotpWizard } from "./TotpWizard.js";

type Dialogs = "none" | "disable" | "codes";

export function SettingsPage(): ReactNode {
  useDocumentTitle("Einstellungen");
  const { state } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const policies = usePolicies();
  const lockCap = policies.maxAutoLockMinutes;
  const autoLock = cappedAutoLock(useAutoLockMinutes(), lockCap);
  const lockOptions = allowedLockOptions(AUTO_LOCK_OPTIONS, lockCap);
  const clipboardSeconds = useClipboardSeconds();
  const lockOnHide = useLockOnHide();
  const breachCheck = useBreachCheck();
  const recentUse = useRecentUse();
  const lock = useLockVault();
  const toast = useToast();
  const queryClient = useQueryClient();
  const twoFactor = useTwoFactorStatus();
  const [wizardOpen, setWizardOpen] = useState(searchParams.get("setup") === "totp");
  const [dialog, setDialog] = useState<Dialogs>("none");
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [proof, setProof] = useState("");
  const [proofError, setProofError] = useState<string | undefined>(undefined);
  const totpOn = twoFactor.data?.totp === "enabled";

  function closeWizardParam(): void {
    setWizardOpen(false);
    if (searchParams.has("setup")) {
      setSearchParams({}, { replace: true });
    }
  }

  async function refreshSecurity(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TWO_FACTOR_KEY }),
      queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY }),
      queryClient.invalidateQueries({ queryKey: ME_KEY })
    ]);
  }

  async function regenerate(): Promise<void> {
    setBusy(true);
    try {
      setCodes(await regenerateBackupCodes());
      setDialog("codes");
      await refreshSecurity();
    } catch {
      toast.notify("Backup-Codes konnten nicht erneuert werden.", { tone: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const value = proof.trim();
    const payload = isCompleteOtp(value) ? { code: value } : { backupCode: value };
    setBusy(true);
    setProofError(undefined);
    try {
      await disableTotp(payload);
      setDialog("none");
      setProof("");
      await refreshSecurity();
      toast.notify("Zwei-Faktor-Schutz deaktiviert.", { tone: "danger" });
    } catch (err: unknown) {
      setProofError(
        err instanceof ApiClientError && err.status === 401
          ? "Code oder Backup-Code ist ungültig."
          : err instanceof ApiClientError && err.status === 409 ? err.message : "Deaktivieren fehlgeschlagen."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page width="narrow">
      <PageHeader eyebrow="Konto" title="Einstellungen & Sicherheit" description={state.status === "locked" ? undefined : state.email} />

      <div className="mt-2">
        <DeviceApprovalsSection />
        <MasterPasswordSection />
        <RecoverySection autoOpen={searchParams.get("setup") === "recovery"} onAutoOpened={closeWizardParam} />
        <AdminRecoverySection />

        <Section
          title="Zwei-Faktor-Authentifizierung"
          description="Ein zweiter Faktor schützt dein Konto, auch wenn das Master-Passwort bekannt wird."
          aside={twoFactor.data !== undefined ? (totpOn ? <Badge tone="mint" icon={ShieldCheck}>Aktiv</Badge> : <Badge tone="buoy">Nicht aktiv</Badge>) : undefined}
        >
          <div className="flex flex-col gap-3">
            {twoFactor.isPending ? <Skeleton className="h-16" /> : null}
            {twoFactor.data !== undefined ? (
              <div className="flex flex-col gap-4 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm leading-relaxed text-mist">
                  <p className="font-medium text-foam">{totpOn ? "Authenticator-App eingerichtet" : "Authenticator-App nicht eingerichtet"}</p>
                  <p className="mt-0.5">{twoFactor.data.backupCodesRemaining} Backup-Codes übrig · {twoFactor.data.webauthnKeys} Passkey(s)</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!totpOn ? (
                    <Button icon={ShieldCheck} onClick={(): void => setWizardOpen(true)}>
                      Einrichten
                    </Button>
                  ) : (
                    <>
                      <Button variant="secondary" icon={LifeBuoy} loading={busy} onClick={(): void => void regenerate()}>
                        Backup-Codes erneuern
                      </Button>
                      <Button variant="danger" onClick={(): void => setDialog("disable")}>
                        Deaktivieren
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : null}
            {totpOn ? null : <p className="text-[0.8125rem] text-mist-2">Nach dem Einrichten erhältst du Backup-Codes. Bewahre sie getrennt von deinem Gerät auf.</p>}
          </div>
        </Section>

        <PasskeysSection />

        <SessionsSection />
        <RememberedDevicesSection />
        <ExportSection />

        <Section
          title="Automatische Sperre"
          description={lockCap === null ? "Der Tresor sperrt sich nach dieser Zeit ohne Eingabe." : `Der Tresor sperrt sich nach dieser Zeit ohne Eingabe. Dein Administrator erlaubt höchstens ${lockCap} Minuten.`}
        >
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-mist">Aktuell nach {autoLock} Minuten Inaktivität.</p>
            <div className="w-full sm:w-48">
              <SelectInput
                aria-label="Automatische Sperre in Minuten"
                value={String(autoLock)}
                onChange={(event): void => {
                  const next = AUTO_LOCK_OPTIONS.find((option: AutoLockMinutes): boolean => String(option) === event.target.value);
                  if (next !== undefined) {
                    setAutoLockMinutes(next);
                  }
                }}
              >
                {lockOptions.map((option: number) => (
                  <option key={option} value={option}>
                    {option} Minuten
                  </option>
                ))}
              </SelectInput>
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-line bg-ink-2 px-4 py-3">
            <Switch
              label="Sperren, wenn der Tab im Hintergrund ist"
              description={`Sperrt nach ${HIDDEN_LOCK_GRACE_MS / 1000} Sekunden, wenn du den Tab wechselst oder das Fenster minimierst.`}
              checked={lockOnHide}
              onChange={setLockOnHide}
            />
          </div>
        </Section>

        <Section
          title="Datenleck-Prüfung"
          description="Gleicht Passwörter mit Have I Been Pwned ab. Dabei verlassen nur die ersten 5 Zeichen des SHA-1-Hashwerts dein Gerät; der Abgleich passiert hier lokal."
        >
          <div className="rounded-lg border border-line bg-ink-2 px-4 py-3">
            <Switch
              label="Passwörter auf bekannte Datenlecks prüfen"
              description="Für die Sicherheitsübersicht und als Warnung beim Speichern. Aus, wenn dein Netz keine Verbindung zu api.pwnedpasswords.com erlaubt."
              checked={breachCheck}
              onChange={setBreachCheck}
            />
          </div>
        </Section>

        <Section title="Zwischenablage" description="Kopierte Passwörter werden danach automatisch aus der Zwischenablage gelöscht – spätestens beim Sperren.">
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-mist">Aktuell nach {clipboardSeconds} Sekunden.</p>
            <div className="w-full sm:w-48">
              <SelectInput
                aria-label="Zwischenablage leeren nach Sekunden"
                value={String(clipboardSeconds)}
                onChange={(event): void => {
                  const next = CLIPBOARD_OPTIONS.find((option: ClipboardSeconds): boolean => String(option) === event.target.value);
                  if (next !== undefined) {
                    setClipboardSeconds(next);
                  }
                }}
              >
                {CLIPBOARD_OPTIONS.map((option: ClipboardSeconds) => (
                  <option key={option} value={option}>
                    {option} Sekunden
                  </option>
                ))}
              </SelectInput>
            </div>
          </div>
        </Section>

        <Section title="Zuletzt verwendet" description="Die Übersicht merkt sich auf diesem Gerät, welche Einträge du zuletzt genutzt hast – nur interne IDs, keine Titel oder Passwörter.">
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-mist">
              {recentUse.length === 0 ? "Kein Verlauf gespeichert." : `${recentUse.length} ${recentUse.length === 1 ? "Eintrag" : "Einträge"} im Verlauf.`}
            </p>
            <Button
              variant="secondary"
              icon={Trash2}
              disabled={recentUse.length === 0}
              onClick={(): void => {
                clearRecentUse();
                toast.notify("Verlauf gelöscht.", { tone: "success" });
              }}
            >
              Verlauf löschen
            </Button>
          </div>
        </Section>

        <Section title="Sitzung" description="Sperre den Tresor, wenn du den Platz verlässt.">
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-mist">Schlüssel werden aus dem Speicher entfernt und die Sitzung beendet.</p>
            <Button variant="secondary" icon={Lock} onClick={(): void => void lock("manual")}>
              Tresor sperren
            </Button>
          </div>
        </Section>
      </div>

      <TotpWizard
        open={wizardOpen}
        onClose={closeWizardParam}
        onDone={(): void => {
          closeWizardParam();
          void refreshSecurity();
          toast.notify("Zwei-Faktor-Schutz ist aktiv.", { tone: "success" });
        }}
      />

      <Dialog open={dialog === "disable"} onClose={(): void => setDialog("none")} title="Zwei-Faktor deaktivieren" description="Bestätige mit einem aktuellen Code oder einem Backup-Code." variant="center" size="sm">
        <form onSubmit={(event): void => void disable(event)} className="flex flex-col gap-5 p-5">
          <Field id="disable-proof" label="Code oder Backup-Code" error={proofError}>
            <TextInput id="disable-proof" value={proof} autoComplete="one-time-code" autoFocus invalid={proofError !== undefined} onChange={(event): void => setProof(event.target.value)} placeholder="123456 oder Backup-Code" className="font-mono tracking-wider" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={(): void => setDialog("none")}>
              Abbrechen
            </Button>
            <Button type="submit" variant="danger" loading={busy} disabled={proof.trim().length < 4}>
              Deaktivieren
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={dialog === "codes"} onClose={(): void => setDialog("none")} title="Neue Backup-Codes" description="Die alten Codes sind ab sofort ungültig. Bewahre die neuen sicher auf." variant="center" size="sm">
        <div className="flex flex-col gap-5 p-5">
          <ul className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-ink p-4 font-mono text-sm text-foam">
            {codes.map((value: string) => (
              <li key={value} className="tracking-wider">
                {value}
              </li>
            ))}
          </ul>
          <Button onClick={(): void => setDialog("none")}>Fertig</Button>
        </div>
      </Dialog>

    </Page>
  );
}
